import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { FirebaseError } from "firebase/app";

import Header from "../../components/header";
import Footer from "../../components/footer";
import SeoHead from "../../components/seoHead";
import { useLocalePath } from "../../i18n/LocaleRouting";
import { useTranslation } from "react-i18next";
import {
  SPEND_OPTIONS,
  TCG_INTEREST_OPTIONS,
  useAuth,
  type SpendRange,
  type TcgInterest,
} from "../../contexts/AuthContext";

type AuthMode = "login" | "register" | "forgot";

const REMEMBERED_EMAIL_KEY = "ftcg_remembered_email";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.2 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.1-3.5 5.5-6.1 7.1l.1.1 6.2 5.2C37.2 41.4 44 36 44 24c0-1.2-.1-2.3-.4-3.5z"
      />
    </svg>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 3l18 18M10.6 10.6A3 3 0 0012 15a3 3 0 002.4-1.2M9.9 5.2A10.5 10.5 0 0112 5c6.5 0 10 7 10 7a18.6 18.6 0 01-4.2 4.8M6.1 6.1A18.3 18.3 0 002 12s3.5 7 10 7c1.4 0 2.7-.3 3.9-.8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function mapAuthError(error: unknown): string {
  const code = (error as FirebaseError)?.code;
  switch (code) {
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Incorrect email or password.";
    case "auth/email-already-in-use":
      return "This email is already registered. Try signing in.";
    case "auth/weak-password":
      return "Password should be at least 6 characters.";
    case "auth/too-many-requests":
      return "Too many attempts. Please try again later.";
    case "auth/popup-closed-by-user":
      return "Google sign-in was cancelled.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      if (error instanceof Error && error.message) return error.message;
      return "Something went wrong. Please try again.";
  }
}

function getRedirectTarget(state: unknown): string {
  if (
    state &&
    typeof state === "object" &&
    "from" in state &&
    typeof (state as { from?: unknown }).from === "string"
  ) {
    const from = (state as { from: string }).from;
    if (from && from !== "/auth") return from;
  }
  return "/";
}

const Auth: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const localePath = useLocalePath();
  const { t } = useTranslation();
  const {
    user,
    loading: authLoading,
    isConfigured,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    resetPassword,
  } = useAuth();

  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(false);
  const [tcgInterest, setTcgInterest] = useState<TcgInterest | "">("");
  const [monthlySpend, setMonthlySpend] = useState<SpendRange | "">("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const redirectTo = useMemo(() => {
    const target = getRedirectTarget(location.state);
    if (target === "/") return localePath("/");
    return target;
  }, [localePath, location.state]);

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBERED_EMAIL_KEY);
    if (saved) {
      setEmail(saved);
      setRememberEmail(true);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) {
      navigate(redirectTo, { replace: true });
    }
  }, [authLoading, user, navigate, redirectTo]);

  const title =
    mode === "register"
      ? "Create your account"
      : mode === "forgot"
        ? "Reset your password"
        : "Sign in to Find TCG Shop";

  const subtitle =
    mode === "register"
      ? "Tell us a bit about your TCG interests to get started."
      : mode === "forgot"
        ? "Enter your email and we’ll send a reset link."
        : "Sign in to your account.";

  const persistRememberedEmail = () => {
    if (rememberEmail && email.trim()) {
      localStorage.setItem(REMEMBERED_EMAIL_KEY, email.trim());
    } else {
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setInfo("");
    setSubmitting(true);

    try {
      if (mode === "forgot") {
        await resetPassword(email);
        persistRememberedEmail();
        setInfo("Password reset email sent. Check your inbox.");
        return;
      }

      if (mode === "register") {
        if (!tcgInterest || !monthlySpend) {
          setError("Please answer both preference questions.");
          return;
        }
        await signUpWithEmail(email, password, {
          tcgInterest,
          monthlySpend,
        });
        persistRememberedEmail();
        return;
      }

      await signInWithEmail(email, password);
      persistRememberedEmail();
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setInfo("");
    setSubmitting(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (next: AuthMode) => {
    setMode(next);
    setError("");
    setInfo("");
    setPassword("");
  };

  return (
    <div className="min-h-screen bg-[#f6f4f1]">
      <SeoHead
        title={t("seo.auth.title")}
        description={t("seo.auth.description")}
        path="/auth"
        noindex
      />
      <Header />

      <main className="flex justify-center px-4 py-10 md:py-14">
        <div className="w-full max-w-[420px] rounded-3xl bg-white px-6 py-8 md:px-8 shadow-[0_12px_40px_rgba(0,0,0,0.08)]">
          <div className="text-center mb-6">
            <h1 className="text-[26px] md:text-[28px] font-bold text-[#111] leading-tight">
              {title}
            </h1>
            <p className="mt-2 text-sm text-[#888]">{subtitle}</p>
          </div>

          {!isConfigured && (
            <div className="mb-4 rounded-xl bg-[#FFF4EF] border border-[#F05A2A]/30 px-3 py-2 text-xs text-[#8a3a1f]">
              Firebase keys are not configured yet. Add{" "}
              <code className="font-mono">REACT_APP_FIREBASE_*</code> values to
              your <code className="font-mono">.env</code> file to enable auth.
            </div>
          )}

          {mode !== "forgot" && (
            <>
              <button
                type="button"
                onClick={handleGoogle}
                disabled={submitting || !isConfigured}
                className="w-full h-11 rounded-xl border border-[#e5e5e5] bg-white flex items-center justify-center gap-2 text-sm font-medium text-[#222] hover:bg-[#fafafa] disabled:opacity-60"
              >
                <GoogleIcon />
                Continue with Google
              </button>

              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#eee]" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-3 text-[#999]">or</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-[#222]">Email</span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-11 rounded-xl bg-[#f7f4ef] px-3 text-sm text-[#222] outline-none ring-0 border border-transparent focus:border-[#F05A2A]/40"
              />
            </label>

            {mode !== "forgot" && (
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-semibold text-[#222]">
                  Password
                </span>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete={
                      mode === "register" ? "new-password" : "current-password"
                    }
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    minLength={6}
                    className="w-full h-11 rounded-xl bg-[#f7f4ef] px-3 pr-10 text-sm text-[#222] outline-none border border-transparent focus:border-[#F05A2A]/40"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888] hover:text-[#555]"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
              </label>
            )}

            {mode === "register" && (
              <>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-[#222]">
                    Which TCG you interested?
                  </span>
                  <select
                    required
                    value={tcgInterest}
                    onChange={(e) =>
                      setTcgInterest(e.target.value as TcgInterest | "")
                    }
                    className="h-11 rounded-xl bg-[#f7f4ef] px-3 text-sm text-[#222] outline-none border border-transparent focus:border-[#F05A2A]/40"
                  >
                    <option value="" disabled>
                      Select a TCG
                    </option>
                    {TCG_INTEREST_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-[#222]">
                    How much you spend on TCG every month?
                  </span>
                  <select
                    required
                    value={monthlySpend}
                    onChange={(e) =>
                      setMonthlySpend(e.target.value as SpendRange | "")
                    }
                    className="h-11 rounded-xl bg-[#f7f4ef] px-3 text-sm text-[#222] outline-none border border-transparent focus:border-[#F05A2A]/40"
                  >
                    <option value="" disabled>
                      Select a range
                    </option>
                    {SPEND_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}

            {mode === "login" && (
              <div className="flex items-center justify-between gap-3 text-sm">
                <label className="flex items-center gap-2 text-[#555] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={rememberEmail}
                    onChange={(e) => setRememberEmail(e.target.checked)}
                    className="accent-[#F05A2A] w-4 h-4"
                  />
                  Remember Email
                </label>
                <button
                  type="button"
                  onClick={() => switchMode("forgot")}
                  className="text-[#F05A2A] font-medium hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            {info && (
              <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                {info}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || !isConfigured}
              className="mt-1 h-11 w-full rounded-xl bg-[#F05A2A] text-white text-sm font-semibold hover:bg-[#e8643b] disabled:opacity-70"
            >
              {submitting
                ? "Please wait..."
                : mode === "register"
                  ? "Create account"
                  : mode === "forgot"
                    ? "Send reset link"
                    : "Sign In"}
            </button>
          </form>

          <div className="mt-5 text-center text-sm text-[#666]">
            {mode === "login" && (
              <>
                Don&apos;t have an account?{" "}
                <button
                  type="button"
                  onClick={() => switchMode("register")}
                  className="text-[#F05A2A] font-semibold hover:underline"
                >
                  Sign up now
                </button>
              </>
            )}
            {mode === "register" && (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => switchMode("login")}
                  className="text-[#F05A2A] font-semibold hover:underline"
                >
                  Sign in
                </button>
              </>
            )}
            {mode === "forgot" && (
              <>
                Remembered your password?{" "}
                <button
                  type="button"
                  onClick={() => switchMode("login")}
                  className="text-[#F05A2A] font-semibold hover:underline"
                >
                  Back to sign in
                </button>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Auth;
